// Game starters (7.6F, ADR-0022): ready-to-edit advergames expressed as
// ordinary typed CreativeOperation[] — the same operation language as every
// Studio edit and as AI authoring (7.6G). A starter adds scenes, layers,
// variables, a timer, rules and the declared `game` module to an existing
// document; the result is a normal Creative Document that the person keeps
// editing. There is no second "game document" and no template backend.
//
// Every layer gets an EXPLICIT frame in every format variant (base values in
// the master variant, overrides elsewhere), computed for that variant's size —
// never a proportional scaling of another variant (CREATIVE-MODEL §4.2).
//
// Placeholder art only (shapes, symbols, text): the person replaces it with
// brand images. Copy is Spanish (the Studio's language); it is ordinary text.
// Nothing here validates: the caller applies the batch with applyAndValidate.

import type {
  CreativeDocument,
  Frame,
  GameDecl,
  Layout,
  Rule,
  StatePatch,
  TextContent,
  TimerDecl,
  VariableDecl,
} from "./schema/document.js";
import type { CreativeOperation } from "./operations.js";
import { expandPreset } from "./presets.js";

export const GAME_STARTER_KINDS = ["find", "pairs", "tap", "quiz", "drag", "runner"] as const;
export type GameStarterKind = (typeof GAME_STARTER_KINDS)[number];

export interface GameStarterOptions {
  /** Accent color written explicitly into the starter (default the Studio accent). */
  readonly accent?: string;
  /** Make the intro scene the scene the creative starts in. */
  readonly makeInitial?: boolean;
  /**
   * Prebuilt formats (report 109): what a person can set without AI before the game is built. Every field is
   * optional and, when absent, the starter is exactly what it always was.
   */
  readonly texts?: GameStarterTexts;
  /** Time to play, ms (games with a countdown; the runner splits it across its stretches). 5 s – 10 min. */
  readonly durationMs?: number;
  /** Memory: number of pairs, 2 – 6. */
  readonly pairs?: number;
  /** Background of the intro, game and result scenes (the runner keeps its skies). */
  readonly background?: string;
}

export interface GameStarterTexts {
  readonly title?: string;
  readonly howTo?: string;
  readonly play?: string;
  readonly winTitle?: string;
  readonly winMessage?: string;
  readonly loseTitle?: string;
  readonly loseMessage?: string;
  /** Label of the button that opens the main link (only when the document has one). */
  readonly cta?: string;
}

/** Limits of the options: a value outside them is clamped, never rejected. */
export const GAME_STARTER_LIMITS = {
  durationMs: { min: 5_000, max: 600_000 },
  pairs: { min: 2, max: 6 },
  text: 120,
} as const;

export interface GameStarter {
  readonly ops: CreativeOperation[];
  readonly introSceneId: string;
  readonly gameSceneIds: readonly string[];
  readonly resultSceneIds: readonly string[];
  readonly game: GameDecl;
}

interface Size {
  readonly width: number;
  readonly height: number;
}
type Json = Record<string, unknown>;

const DARK = "#111827";
const LIGHT = "#F3F4F6";
const WHITE = "#FFFFFF";
const MUTED = "#E5E7EB";
const DEFAULT_ACCENT = "#E4002B";
const PAIR_COLORS = ["#E4002B", "#2563EB", "#10B981", "#F59E0B", "#7C3AED", "#0EA5E9"];
const PAIR_SYMBOLS = ["★", "●", "▲", "■", "◆", "♥"];

const sizeOf = (l: Layout): Size =>
  l.sizing.mode === "fixed"
    ? { width: l.sizing.width, height: l.sizing.height }
    : { width: l.sizing.design.width, height: l.sizing.design.height };

const r = Math.round;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const playTime = (ms: number) => r(clamp(ms, GAME_STARTER_LIMITS.durationMs.min, GAME_STARTER_LIMITS.durationMs.max));
const T = (text: string): TextContent => ({ runs: [{ text }] });
const frameOf = (x: number, y: number, width: number, height: number): Frame => ({
  x: r(x),
  y: r(y),
  width: Math.max(8, r(width)),
  height: Math.max(8, r(height)),
});

/** Height of the score/time band at the top of a game scene. */
const band = (s: Size) => clamp(r(s.height * 0.13), 20, 36);
const pad = (s: Size) => clamp(r(Math.min(s.width, s.height) * 0.04), 6, 16);

interface LayerSpec {
  readonly element: Json;
  readonly frame: (s: Size) => Frame;
  /** Font size per variant (text, button). */
  readonly font?: (s: Size) => number;
}

class Builder {
  readonly ops: CreativeOperation[] = [];
  readonly #taken: Set<string>;
  readonly #layouts: ReadonlyArray<{ readonly id: string; readonly size: Size }>;
  readonly tune: GameStarterOptions;

  constructor(doc: CreativeDocument, tune: GameStarterOptions = {}) {
    this.tune = tune;
    this.#taken = new Set([
      ...doc.elements.map((e) => e.id),
      ...doc.scenes.map((s) => s.id),
      ...(doc.variables ?? []).map((v) => v.id),
      ...(doc.timers ?? []).map((t) => t.id),
      ...(doc.rules ?? []).map((x) => x.id),
      ...doc.layouts.map((l) => l.id),
      ...(doc.events ?? []).map((e) => e.name),
    ]);
    this.#layouts = doc.layouts.map((l) => ({ id: l.id, size: sizeOf(l) }));
  }

  /** A document-unique id with a readable prefix ("score", "score2"…). */
  id(prefix: string): string {
    let candidate = prefix;
    for (let n = 2; this.#taken.has(candidate); n++) candidate = `${prefix}${String(n)}`;
    this.#taken.add(candidate);
    return candidate;
  }

  scene(prefix: string, name: string, role: "content" | "game" | "result", background: string): string {
    const id = this.id(prefix);
    // A chosen background replaces only the neutral ones: the runner's skies stay.
    if (this.tune.background !== undefined && (background === WHITE || background === LIGHT)) background = this.tune.background;
    this.ops.push({
      op: "addScene",
      scene: { id, name, role, duration: null, background: { kind: "solid", color: background }, children: [] },
    });
    return id;
  }

  /** Adds a layer to a scene with an explicit frame (and font size) in EVERY variant. */
  layer(sceneId: string, spec: LayerSpec): string {
    const [master, ...others] = this.#layouts;
    if (!master) throw new Error("document without layouts");
    const base = { ...spec.element, frame: spec.frame(master.size) } as Json;
    if (spec.font) base.typography = { ...(base.typography as Json), size: spec.font(master.size) };
    this.ops.push({
      op: "addElement",
      element: base as never,
      parent: { in: "scene", id: sceneId },
    });
    const id = base.id as string;
    for (const l of others) {
      const frame = spec.frame(l.size);
      const override: Json = { frame };
      const size = spec.font?.(l.size);
      if (size !== undefined && size !== (base.typography as Json).size) override.typography = { size };
      this.ops.push({ op: "setLayoutOverride", layout: l.id, element: id, override: override });
    }
    return id;
  }

  /** Layout ids and sizes (master first): runner speeds are explicit per format. */
  layoutSizes(): ReadonlyArray<{ readonly id: string; readonly size: Size }> {
    return this.#layouts;
  }

  variable(v: VariableDecl): void {
    this.ops.push({ op: "setVariable", variable: v });
  }
  timer(t: TimerDecl): void {
    // The countdown of a game (its id starts with "gameTime") takes the chosen time; helper timers never do.
    const chosen = this.tune.durationMs;
    const timed = chosen !== undefined && t.id.startsWith("gameTime") ? { ...t, durationMs: playTime(chosen) } : t;
    this.ops.push({ op: "setTimer", timer: timed });
  }
  /** A text the person chose, or the starter's own. */
  text(key: keyof GameStarterTexts, fallback: string): string {
    const v = this.tune.texts?.[key]?.trim();
    return v ? v.slice(0, GAME_STARTER_LIMITS.text) : fallback;
  }
  rule(rule: Rule): void {
    this.ops.push({ op: "addRule", rule });
  }
}

// ─── Shared pieces ──────────────────────────────────────────────────────────

const textLayer = (
  id: string,
  name: string,
  content: TextContent,
  color: string,
  weight: number,
  frame: (s: Size) => Frame,
  font: (s: Size) => number,
  align: "left" | "center" | "right" = "center"
): LayerSpec => ({
  element: {
    id,
    type: "text",
    name,
    content,
    typography: { size: 12, weight, color, align, verticalAlign: "middle", lineHeight: 1.15 },
  },
  frame,
  font,
});

const buttonLayer = (
  id: string,
  name: string,
  label: string,
  fill: string,
  color: string,
  frame: (s: Size) => Frame,
  role: "cta" | "button" = "button"
): LayerSpec => ({
  element: {
    id,
    type: "button",
    name,
    role,
    label: T(label),
    typography: { size: 12, weight: 700, color, align: "center", verticalAlign: "middle" },
    style: { fill: { kind: "solid", color: fill }, radius: 6 },
    interactive: { cursor: "pointer" },
  },
  frame,
  font: (s) => clamp(r(Math.min(s.width, s.height) / 16), 11, 18),
});

/** Score text (top-left) and, with a timer, a countdown (top-right). */
function hud(b: Builder, scene: string, label: string, scoreVar: string, suffix: string, timer?: string) {
  b.layer(
    scene,
    textLayer(
      b.id("scoreText"),
      "Marcador",
      {
        runs: [
          { text: `${label} ` },
          { bind: { var: scoreVar, format: "integer" } },
          ...(suffix ? [{ text: suffix }] : []),
        ],
      },
      DARK,
      700,
      (s) => frameOf(pad(s), pad(s) / 2, s.width * 0.55, band(s)),
      (s) => clamp(r(band(s) * 0.55), 10, 20),
      "left"
    )
  );
  if (timer === undefined) return;
  b.layer(scene, {
    element: {
      id: b.id("countdown"),
      type: "component",
      name: "Tiempo",
      component: "ia.countdown@1",
      props: { timer, format: "mm:ss", showZero: true, color: DARK },
      interactive: { label: "Tiempo restante" },
    },
    frame: (s) => {
      const w = band(s) * 2.6;
      return frameOf(s.width - pad(s) - w, pad(s) / 2, w, band(s));
    },
  });
}

function intro(b: Builder, title: string, howTo: string, accent: string): { scene: string; play: string } {
  const scene = b.scene("intro", "Instrucciones", "content", WHITE);
  b.layer(
    scene,
    textLayer(
      b.id("introTitle"),
      "Título",
      T(b.text("title", title)),
      DARK,
      800,
      (s) => frameOf(pad(s) * 2, s.height * 0.12, s.width - pad(s) * 4, s.height * 0.28),
      (s) => clamp(r(Math.min(s.width / 12, s.height / 8)), 14, 34)
    )
  );
  b.layer(
    scene,
    textLayer(
      b.id("introHowTo"),
      "Cómo se juega",
      T(b.text("howTo", howTo)),
      "#374151",
      500,
      (s) => frameOf(pad(s) * 2, s.height * 0.42, s.width - pad(s) * 4, s.height * 0.22),
      (s) => clamp(r(Math.min(s.width / 22, s.height / 14)), 10, 18)
    )
  );
  const play = b.layer(
    scene,
    buttonLayer(b.id("playButton"), "Jugar", b.text("play", "Jugar"), accent, WHITE, (s) => {
      const w = clamp(s.width * 0.4, 96, 220);
      const h = clamp(s.height * 0.16, 30, 48);
      return frameOf((s.width - w) / 2, s.height * 0.7, w, h);
    })
  );
  return { scene, play };
}

function results(
  b: Builder,
  firstGameScene: string,
  scoreVar: string,
  scoreLabel: string,
  accent: string,
  link: string | undefined
): { win: string; lose: string } {
  const make = (prefix: string, name: string, title: string, message: string) => {
    const scene = b.scene(prefix, name, "result", WHITE);
    b.layer(
      scene,
      textLayer(
        b.id(`${prefix}Title`),
        "Título",
        T(title),
        DARK,
        800,
        (s) => frameOf(pad(s) * 2, s.height * 0.1, s.width - pad(s) * 4, s.height * 0.26),
        (s) => clamp(r(Math.min(s.width / 10, s.height / 7)), 16, 40)
      )
    );
    b.layer(
      scene,
      textLayer(
        b.id(`${prefix}Score`),
        "Resultado",
        { runs: [{ text: `${message} ${scoreLabel} ` }, { bind: { var: scoreVar, format: "integer" } }] },
        "#374151",
        600,
        (s) => frameOf(pad(s) * 2, s.height * 0.38, s.width - pad(s) * 4, s.height * 0.16),
        (s) => clamp(r(Math.min(s.width / 20, s.height / 12)), 10, 20)
      )
    );
    const wide = (s: Size) => s.width / s.height > 1.6;
    const btn = (s: Size, i: number) => {
      const w = clamp(s.width * (wide(s) ? 0.22 : 0.4), 96, 220);
      const h = clamp(s.height * 0.15, 28, 46);
      if (link === undefined) return frameOf((s.width - w) / 2, s.height * 0.66, w, h);
      if (wide(s)) return frameOf(s.width / 2 + (i === 0 ? -w - pad(s) / 2 : pad(s) / 2), s.height * 0.66, w, h);
      return frameOf((s.width - w) / 2, s.height * (i === 0 ? 0.58 : 0.78), w, h);
    };
    const replay = b.layer(
      scene,
      buttonLayer(b.id(`${prefix}Replay`), "Jugar de nuevo", "Jugar de nuevo", DARK, WHITE, (s) => btn(s, 0))
    );
    b.rule({
      id: b.id(`${prefix}ReplayTap`),
      on: { type: "pointer.tap", target: replay },
      do: [{ type: "game.restart", scene: firstGameScene }],
    });
    if (link !== undefined) {
      const cta = b.layer(
        scene,
        buttonLayer(b.id(`${prefix}Cta`), "Ver más", b.text("cta", "Ver más"), accent, WHITE, (s) => btn(s, 1), "cta")
      );
      b.rule({
        id: b.id(`${prefix}CtaTap`),
        on: { type: "pointer.tap", target: cta },
        do: [{ type: "link.open", link }],
      });
    }
    return scene;
  };
  return {
    win: make("win", "Ganaste", b.text("winTitle", "¡Ganaste!"), b.text("winMessage", "¡Bien hecho!")),
    lose: make("lose", "Fin del juego", b.text("loseTitle", "¡Casi!"), b.text("loseMessage", "Inténtalo otra vez.")),
  };
}

const scoreVar = (id: string, max = 9_999): VariableDecl => ({
  id,
  type: "number",
  initial: 0,
  min: 0,
  max,
  integer: true,
  report: "onComplete",
});
const livesVar = (id: string): VariableDecl => ({ id, type: "number", initial: 3, min: 0, max: 3, integer: true });

/** Play area below the score band. */
const area = (s: Size): Frame => {
  const top = band(s) + pad(s);
  return frameOf(pad(s), top, s.width - pad(s) * 2, s.height - top - pad(s));
};

/** Best grid (columns) for `n` cells of aspect w/h ≈ `aspect` inside `a`. */
function grid(a: Frame, n: number, aspect: number, gap: number) {
  let best = { cols: 1, rows: n, cw: 0, ch: 0 };
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const cellW = (a.width - gap * (cols - 1)) / cols;
    const cellH = (a.height - gap * (rows - 1)) / rows;
    const ch = Math.min(cellH, cellW / aspect);
    if (ch > best.ch) best = { cols, rows, cw: ch * aspect, ch };
  }
  const totalW = best.cols * best.cw + gap * (best.cols - 1);
  const totalH = best.rows * best.ch + gap * (best.rows - 1);
  const x0 = a.x + (a.width - totalW) / 2;
  const y0 = a.y + (a.height - totalH) / 2;
  return (i: number): Frame => {
    const c = i % best.cols;
    const row = Math.floor(i / best.cols);
    return frameOf(x0 + c * (best.cw + gap), y0 + row * (best.ch + gap), best.cw, best.ch);
  };
}

// ─── Starters ───────────────────────────────────────────────────────────────

interface Built {
  readonly game: GameDecl;
  readonly intro: string;
  readonly scenes: readonly string[];
  readonly results: readonly string[];
}

function findTheDifference(b: Builder, accent: string, link: string | undefined): Built {
  const score = b.id("found");
  const lives = b.id("lives");
  const timer = b.id("gameTime");
  b.variable(scoreVar(score, 5));
  b.variable(livesVar(lives));
  b.timer({ id: timer, durationMs: 45_000, autoStart: false });
  const start = intro(b, "Encuentra las 5 diferencias", "Toca las diferencias en la imagen de la derecha.", accent);
  const scene = b.scene("game", "Juego", "game", WHITE);
  b.rule({
    id: b.id("introPlay"),
    on: { type: "pointer.tap", target: start.play },
    do: [{ type: "scene.goTo", scene }],
  });
  hud(b, scene, "Diferencias:", score, "/5", timer);
  // Two panels: side by side in wide formats, stacked in tall ones.
  const panel = (s: Size, i: 0 | 1): Frame => {
    const a = area(s);
    const g = pad(s);
    if (s.width / s.height >= 1.3) {
      const w = (a.width - g) / 2;
      return frameOf(a.x + i * (w + g), a.y, w, a.height);
    }
    const h = (a.height - g) / 2;
    return frameOf(a.x, a.y + i * (h + g), a.width, h);
  };
  const panelLayer = (i: 0 | 1, name: string) =>
    b.layer(scene, {
      element: {
        id: b.id(i === 0 ? "original" : "changed"),
        type: "shape",
        name,
        shape: "rect",
        style: { fill: { kind: "solid", color: MUTED }, radius: 8 },
      },
      frame: (s) => panel(s, i),
    });
  panelLayer(0, "Imagen original");
  panelLayer(1, "Imagen con diferencias");
  const caption = (i: 0 | 1, text: string) =>
    b.layer(
      scene,
      textLayer(
        b.id("panelCaption"),
        "Indicación",
        T(text),
        "#6B7280",
        600,
        (s) => {
          const p = panel(s, i);
          return frameOf(
            p.x + 4,
            p.y + p.height - clamp(p.height * 0.18, 14, 24) - 4,
            p.width - 8,
            clamp(p.height * 0.18, 14, 24)
          );
        },
        (s) => clamp(r(Math.min(s.width, s.height) / 26), 9, 13)
      )
    );
  caption(0, "Original");
  caption(1, "Encuentra las diferencias");
  // The miss area (behind the differences): a tap that finds nothing costs a life.
  const miss = b.layer(scene, {
    element: {
      id: b.id("missArea"),
      type: "hotspot",
      name: "Fallo (imagen)",
      shape: "rect",
      interactive: { cursor: "pointer", focusable: false, label: "Imagen con diferencias" },
    },
    frame: (s) => panel(s, 1),
  });
  const spots = [
    [0.2, 0.22],
    [0.72, 0.18],
    [0.46, 0.5],
    [0.18, 0.7],
    [0.78, 0.66],
  ] as const;
  const found: StatePatch = {
    border: { width: 3, color: accent, style: "solid" },
    fill: { kind: "solid", color: `${accent}33` },
  };
  const targets = spots.map(([fx, fy], i) =>
    b.layer(scene, {
      element: {
        id: b.id("difference"),
        type: "hotspot",
        name: `Diferencia ${String(i + 1)}`,
        shape: "ellipse",
        style: { radius: 999 },
        states: { found },
        interactive: { cursor: "pointer", focusable: true, label: `Diferencia ${String(i + 1)}` },
      },
      frame: (s) => {
        const p = panel(s, 1);
        const d = clamp(Math.min(p.width, p.height) * 0.22, 24, 64);
        return frameOf(p.x + fx * (p.width - d), p.y + fy * (p.height - d), d, d);
      },
    })
  );
  const res = results(b, scene, score, "Diferencias encontradas:", accent, link);
  return {
    intro: start.scene,
    scenes: [scene],
    results: [res.win, res.lose],
    game: {
      id: b.id("findGame"),
      name: "Encuentra las diferencias",
      scenes: [scene],
      start: "onEnter",
      score,
      lives,
      timer,
      win: { when: "cleared" },
      scoring: { hit: 1, miss: 0, missCostsLife: true },
      results: { win: res.win, lose: res.lose, delayMs: 600 },
      targets: [
        { element: miss, role: "wrong" },
        ...targets.map((element) => ({ element, role: "correct" as const, foundState: "found" })),
      ],
    },
  };
}

function matchingPairs(b: Builder, accent: string, link: string | undefined): Built {
  const score = b.id("pairsFound");
  const timer = b.id("gameTime");
  const n = clamp(r(b.tune.pairs ?? 4), GAME_STARTER_LIMITS.pairs.min, GAME_STARTER_LIMITS.pairs.max);
  b.variable(scoreVar(score, n));
  b.timer({ id: timer, durationMs: 60_000, autoStart: false });
  const start = intro(
    b,
    "Encuentra las parejas",
    "Toca dos cartas para darles la vuelta. ¡Encuentra todas las parejas!",
    accent
  );
  const scene = b.scene("game", "Juego", "game", LIGHT);
  b.rule({
    id: b.id("introPlay"),
    on: { type: "pointer.tap", target: start.play },
    do: [{ type: "scene.goTo", scene }],
  });
  hud(b, scene, "Parejas:", score, `/${String(n)}`, timer);
  const cell = (s: Size, i: number) => grid(area(s), n * 2, 0.8, pad(s))(i);
  const cards: string[] = [];
  for (let i = 0; i < n * 2; i++) {
    const pairIndex = Math.floor(i / 2);
    cards.push(
      b.layer(scene, {
        element: {
          id: b.id("card"),
          type: "text",
          name: `Carta ${String(i + 1)}`,
          content: T(PAIR_SYMBOLS[pairIndex] ?? "★"),
          typography: { size: 12, weight: 700, color: WHITE, align: "center", verticalAlign: "middle", lineHeight: 1 },
          style: { fill: { kind: "solid", color: PAIR_COLORS[pairIndex] ?? accent }, radius: 8 },
          states: { matched: { opacity: 0.55 } },
          interactive: { cursor: "pointer", focusable: true, label: `Carta ${String(i + 1)}` },
        },
        frame: (s) => cell(s, i),
        font: (s) => clamp(r(cell(s, i).height * 0.45), 12, 72),
      })
    );
  }
  const res = results(b, scene, score, "Parejas encontradas:", accent, link);
  return {
    intro: start.scene,
    scenes: [scene],
    results: [res.win, res.lose],
    game: {
      id: b.id("pairsGame"),
      name: "Encuentra las parejas",
      scenes: [scene],
      start: "onEnter",
      score,
      timer,
      win: { when: "cleared" },
      scoring: { hit: 1, miss: 0, missCostsLife: false },
      results: { win: res.win, lose: res.lose, delayMs: 600 },
      pairs: {
        cards: Array.from({ length: n }, (_, p) => [cards[p * 2] as string, cards[p * 2 + 1] as string]),
        cover: { color: "#1F2937", accent },
        mismatchMs: 900,
        shuffle: true,
        matchedState: "matched",
      },
    },
  };
}

function tapCatch(b: Builder, accent: string, link: string | undefined): Built {
  const score = b.id("points");
  const timer = b.id("gameTime");
  const hop = b.id("hop");
  b.variable(scoreVar(score));
  b.timer({ id: timer, durationMs: 20_000, autoStart: false });
  b.timer({ id: hop, durationMs: 1_100, repeat: true, autoStart: false });
  const start = intro(b, "¡Atrápalo!", "Toca la estrella antes de que salte. Evita la ✕. Consigue 10 puntos.", accent);
  const scene = b.scene("game", "Juego", "game", LIGHT);
  b.rule({
    id: b.id("introPlay"),
    on: { type: "pointer.tap", target: start.play },
    do: [{ type: "scene.goTo", scene }],
  });
  // The area first (back-most): where targets land when they jump.
  const playArea = b.layer(scene, {
    element: {
      id: b.id("playArea"),
      type: "hotspot",
      name: "Área de juego",
      shape: "rect",
      interactive: { focusable: false, label: "Área de juego" },
    },
    frame: area,
  });
  hud(b, scene, "Puntos:", score, "", timer);
  const token = (prefix: string, name: string, symbol: string, fill: string, fx: number) =>
    b.layer(scene, {
      element: {
        id: b.id(prefix),
        type: "text",
        name,
        content: T(symbol),
        typography: { size: 12, weight: 800, color: WHITE, align: "center", verticalAlign: "middle", lineHeight: 1 },
        style: { fill: { kind: "solid", color: fill }, radius: 999 },
        interactive: { cursor: "pointer", focusable: true, label: name },
      },
      frame: (s) => {
        const a = area(s);
        const d = clamp(Math.min(s.width, s.height) * 0.2, 32, 64);
        return frameOf(a.x + fx * (a.width - d), a.y + (a.height - d) / 2, d, d);
      },
      font: (s) => clamp(r(clamp(Math.min(s.width, s.height) * 0.2, 32, 64) * 0.5), 14, 34),
    });
  const star = token("star", "Estrella", "★", accent, 0.3);
  const decoy = token("decoy", "Trampa", "✕", "#6B7280", 0.7);
  b.rule({
    id: b.id("hopStart"),
    on: { type: "game.event", event: "start" },
    do: [{ type: "timer.start", timer: hop }],
  });
  b.rule({
    id: b.id("hopMove"),
    on: { type: "timer.elapsed", timer: hop },
    do: [
      { type: "element.relocate", target: star, area: playArea },
      { type: "element.relocate", target: decoy, area: playArea },
    ],
  });
  const res = results(b, scene, score, "Puntos:", accent, link);
  return {
    intro: start.scene,
    scenes: [scene],
    results: [res.win, res.lose],
    game: {
      id: b.id("tapGame"),
      name: "¡Atrápalo!",
      scenes: [scene],
      start: "onEnter",
      score,
      timer,
      win: { when: "score", atLeast: 10, immediate: true },
      scoring: { hit: 1, miss: -1, missCostsLife: false },
      results: { win: res.win, lose: res.lose, delayMs: 400 },
      targets: [
        { element: star, role: "correct", repeat: true, relocate: playArea },
        { element: decoy, role: "wrong", relocate: playArea },
      ],
    },
  };
}

function quiz(b: Builder, accent: string, link: string | undefined): Built {
  const score = b.id("correct");
  const n = 3;
  b.variable(scoreVar(score, n));
  const start = intro(b, "¿Cuánto sabes?", "Responde 3 preguntas. Acierta al menos 2 para ganar.", accent);
  const questions = [
    { q: "Pregunta 1: escribe aquí tu pregunta", answers: ["Opción A", "Opción B", "Opción C"], correct: 1 },
    { q: "Pregunta 2: escribe aquí tu pregunta", answers: ["Opción A", "Opción B", "Opción C"], correct: 0 },
    { q: "Pregunta 3: escribe aquí tu pregunta", answers: ["Opción A", "Opción B", "Opción C"], correct: 2 },
  ];
  const scenes = questions.map((_, i) => b.scene("question", `Pregunta ${String(i + 1)}`, "game", WHITE));
  b.rule({
    id: b.id("introPlay"),
    on: { type: "pointer.tap", target: start.play },
    do: [{ type: "scene.goTo", scene: scenes[0] as string }],
  });
  const targets: Array<NonNullable<GameDecl["targets"]>[number]> = [];
  const fade = { kind: "fade" as const, duration: 250, easing: "easeOut" as const };
  questions.forEach((question, qi) => {
    const scene = scenes[qi] as string;
    hud(b, scene, "Aciertos:", score, "");
    b.layer(
      scene,
      textLayer(
        b.id("questionText"),
        "Pregunta",
        T(question.q),
        DARK,
        700,
        (s) => {
          const a = area(s);
          return frameOf(a.x, a.y, a.width, a.height * 0.34);
        },
        (s) => clamp(r(Math.min(s.width / 18, s.height / 11)), 11, 22)
      )
    );
    question.answers.forEach((answer, ai) => {
      const frame = (s: Size) => {
        const a = area(s);
        const top = a.y + a.height * 0.4;
        const h = a.height * 0.6;
        const g = pad(s) / 2;
        if (s.width / s.height >= 2) {
          const w = (a.width - g * 2) / 3;
          return frameOf(a.x + ai * (w + g), top + h * 0.15, w, h * 0.6);
        }
        const bh = (h - g * 2) / 3;
        return frameOf(a.x + a.width * 0.1, top + ai * (bh + g), a.width * 0.8, bh);
      };
      const id = b.layer(scene, buttonLayer(b.id("answer"), answer, answer, MUTED, DARK, frame));
      targets.push({ element: id, role: ai === question.correct ? "correct" : "wrong" });
      const last = qi === questions.length - 1;
      b.rule({
        id: b.id("answerTap"),
        on: { type: "pointer.tap", target: id },
        do: last
          ? [{ type: "game.end", outcome: "auto" }]
          : [{ type: "scene.goTo", scene: scenes[qi + 1] as string, transition: fade }],
      });
    });
  });
  const res = results(b, scenes[0] as string, score, "Aciertos:", accent, link);
  return {
    intro: start.scene,
    scenes,
    results: [res.win, res.lose],
    game: {
      id: b.id("quizGame"),
      name: "Quiz",
      scenes,
      start: "onEnter",
      score,
      win: { when: "score", atLeast: 2, immediate: false },
      scoring: { hit: 1, miss: 0, missCostsLife: false },
      results: { win: res.win, lose: res.lose, delayMs: 300 },
      targets,
    },
  };
}

function dragToTarget(b: Builder, accent: string, link: string | undefined): Built {
  const score = b.id("placed");
  const lives = b.id("lives");
  const timer = b.id("gameTime");
  const n = 3;
  b.variable(scoreVar(score, n));
  b.variable(livesVar(lives));
  b.timer({ id: timer, durationMs: 30_000, autoStart: false });
  const start = intro(
    b,
    "Ponlo en su lugar",
    "Arrastra cada pieza a la caja de su color. Con teclado: Enter para tomar y Enter para soltar.",
    accent
  );
  const scene = b.scene("game", "Juego", "game", WHITE);
  b.rule({
    id: b.id("introPlay"),
    on: { type: "pointer.tap", target: start.play },
    do: [{ type: "scene.goTo", scene }],
  });
  hud(b, scene, "Colocadas:", score, `/${String(n)}`, timer);
  const colors = ["#E4002B", "#2563EB", "#10B981"];
  const names = ["Rojo", "Azul", "Verde"];
  const wide = (s: Size) => s.width / s.height >= 1.6;
  // Items on the left/top half, zones on the right/bottom half.
  const slot = (s: Size, side: 0 | 1, i: number, item: boolean): Frame => {
    const a = area(s);
    const half = wide(s)
      ? frameOf(a.x + side * (a.width / 2), a.y, a.width / 2, a.height)
      : frameOf(a.x, a.y + side * (a.height / 2), a.width, a.height / 2);
    const g = pad(s);
    const w = (half.width - g * (n + 1)) / n;
    const side2 = Math.min(w, half.height - g * 2);
    // A placed piece (snapped to the center) leaves the box's label visible at its bottom.
    const d = item ? side2 * 0.56 : side2;
    const cx = half.x + g + i * (w + g) + w / 2;
    const cy = half.y + half.height / 2;
    return frameOf(cx - d / 2, cy - d / 2, d, d);
  };
  const zones = colors.map((c, i) =>
    b.layer(scene, {
      element: {
        id: b.id("zone"),
        type: "text",
        name: `Caja ${names[i] ?? ""}`,
        content: T(names[i] ?? ""),
        typography: { size: 12, weight: 600, color: c, align: "center", verticalAlign: "bottom", lineHeight: 1.1 },
        style: {
          fill: { kind: "solid", color: `${c}14` },
          border: { width: 2, color: c, style: "dashed" },
          radius: 10,
        },
        interactive: { label: `Caja ${names[i] ?? ""}` },
      },
      frame: (s) => slot(s, 1, i, false),
      font: (s) => clamp(r(slot(s, 1, i, false).height * 0.16), 9, 16),
    })
  );
  const items = colors.map((c, i) =>
    b.layer(scene, {
      element: {
        id: b.id("piece"),
        type: "text",
        name: `Pieza ${names[i] ?? ""}`,
        content: T(String(i + 1)),
        typography: { size: 12, weight: 800, color: WHITE, align: "center", verticalAlign: "middle", lineHeight: 1 },
        style: { fill: { kind: "solid", color: c }, radius: 999 },
        behaviors: [{ type: "draggable", bounds: "layout", snapBack: true, dropTargets: [zones[i] as string] }],
        interactive: { cursor: "pointer", focusable: true, label: `Pieza ${names[i] ?? ""}` },
      },
      frame: (s) => slot(s, 0, i, true),
      font: (s) => clamp(r(slot(s, 0, i, true).height * 0.45), 12, 40),
    })
  );
  const res = results(b, scene, score, "Piezas colocadas:", accent, link);
  return {
    intro: start.scene,
    scenes: [scene],
    results: [res.win, res.lose],
    game: {
      id: b.id("dragGame"),
      name: "Ponlo en su lugar",
      scenes: [scene],
      start: "onEnter",
      score,
      lives,
      timer,
      win: { when: "cleared" },
      scoring: { hit: 1, miss: 0, missCostsLife: true },
      results: { win: res.win, lose: res.lose, delayMs: 600 },
      drops: { items, zones },
    },
  };
}

// ─── Runner / platformer (7.6F.1, ADR-0023) ────────────────────────────────
//
// Generic placeholders only (shapes and symbols, never brand or third-party
// game art): a player, three environment segments with their own sky and
// background (day → sunset → night), a looping ground, low hazards to jump,
// high hazards to duck under, energy pickups, an energy bar, jump/duck
// controls. Every frame is explicit per format and computed from that
// format's size (tall formats keep the controls under the ground; wide ones
// put them on the ground band).

const SKIES = ["#BFDBFE", "#FDBA74", "#1E3A8A"] as const;
const SCENERY = ["#64748B", "#A16207", "#334155"] as const;
const SEGMENT_NAMES = ["Escenario 1", "Escenario 2", "Escenario 3"] as const;

interface RunnerFrames {
  readonly groundY: number;
  readonly playerW: number;
  readonly playerH: number;
  readonly playerX: number;
}
function runnerFrames(s: Size): RunnerFrames {
  const tall = s.height >= s.width * 1.2;
  const groundY = r(s.height * (tall ? 0.74 : 0.78));
  const playerH = r(clamp(Math.min(s.height * 0.16, s.width * 0.28, groundY - band(s) - pad(s) * 2), 24, 110));
  const playerW = r(playerH * 0.62);
  return { groundY, playerW, playerH, playerX: r(s.width * 0.16) };
}

function runner(b: Builder, accent: string, link: string | undefined): Built {
  const score = b.id("points");
  const energy = b.id("energy");
  b.variable(scoreVar(score));
  b.variable({ id: energy, type: "number", initial: 100, min: 0, max: 100, integer: true });
  const start = intro(
    b,
    "¡A correr!",
    "Salta los obstáculos bajos (Espacio o ↑) y agáchate ante los altos (↓). Recoge energía hasta la meta.",
    accent
  );
  const scene = b.scene("run", "Carrera", "game", SKIES[0]);
  b.rule({
    id: b.id("introPlay"),
    on: { type: "pointer.tap", target: start.play },
    do: [{ type: "scene.goTo", scene }],
  });
  const full = (s: Size) => frameOf(0, 0, s.width, s.height);
  const shape = (prefix: string, name: string, fill: Json, frame: (s: Size) => Frame, extra: Json = {}): string =>
    b.layer(scene, {
      element: { id: b.id(prefix), type: "shape", name, shape: "rect", style: { fill }, ...extra },
      frame,
    });

  // Back to front: skies (one per segment), the background of each segment, the ground. The
  // first environment is the topmost of each kind, so the editor shows the run as it starts;
  // while playing, only the current environment's scenery is visible (cross-faded).
  const skies: string[] = [];
  for (let i = SKIES.length - 1; i >= 0; i--)
    skies[i] = shape("sky", `Cielo ${String(i + 1)}`, { kind: "solid", color: SKIES[i] }, full);
  const scenery: string[] = [];
  for (let i = SCENERY.length - 1; i >= 0; i--) {
    const c = SCENERY[i] as string;
    scenery[i] = shape(
      "scenery",
      `Fondo de ${SEGMENT_NAMES[i] ?? ""}`,
      {
        kind: "linear",
        angle: 90,
        // Vertical bands (a placeholder skyline): the parallax is visible while it scrolls.
        stops: [
          { at: 0, color: `${c}D9` },
          { at: 0.125, color: `${c}D9` },
          { at: 0.125, color: `${c}80` },
          { at: 0.375, color: `${c}80` },
          { at: 0.375, color: `${c}D9` },
          { at: 0.625, color: `${c}D9` },
          { at: 0.625, color: `${c}80` },
          { at: 1, color: `${c}80` },
        ],
      },
      (sz) => {
        const g = runnerFrames(sz).groundY;
        return frameOf(0, g * 0.45, sz.width * 2, g - g * 0.45);
      }
    );
  }
  const ground = shape(
    "ground",
    "Suelo",
    {
      kind: "linear",
      angle: 90,
      stops: [
        { at: 0, color: "#4B5563" },
        { at: 0.25, color: "#4B5563" },
        { at: 0.25, color: "#6B7280" },
        { at: 0.5, color: "#6B7280" },
        { at: 0.5, color: "#4B5563" },
        { at: 0.75, color: "#4B5563" },
        { at: 0.75, color: "#6B7280" },
        { at: 1, color: "#6B7280" },
      ],
    },
    (s) => {
      const g = runnerFrames(s).groundY;
      return frameOf(0, g, s.width * 2, s.height - g);
    }
  );

  // The player: a placeholder the person replaces with their character (Studio: "Reemplazar por una imagen").
  const hitTrack = b.id("hitShake");
  const player = b.layer(scene, {
    element: {
      id: b.id("player"),
      type: "shape",
      name: "Personaje",
      shape: "rect",
      style: { fill: { kind: "solid", color: accent }, radius: 10 },
      states: {
        duck: { fill: { kind: "solid", color: `${accent}B3` } },
        hit: { border: { width: 3, color: "#FFFFFF", style: "solid" } },
      },
      interactive: { label: "Personaje" },
      animations: [
        {
          ...expandPreset({ preset: "shake", trackId: hitTrack, at: 0, duration: 420, distance: 6 }),
          start: { mode: "manual" },
        },
      ],
    },
    frame: (s) => {
      const f = runnerFrames(s);
      return frameOf(f.playerX, f.groundY - f.playerH, f.playerW, f.playerH);
    },
  });

  // Templates: their frame in each format is the LANE where instances appear (x = shown next to the right edge).
  const token = (
    prefix: string,
    name: string,
    symbol: string,
    fill: string,
    lane: (f: RunnerFrames, s: Size) => Frame
  ) =>
    b.layer(scene, {
      element: {
        id: b.id(prefix),
        type: "text",
        name,
        template: true,
        content: T(symbol),
        typography: { size: 12, weight: 800, color: WHITE, align: "center", verticalAlign: "middle", lineHeight: 1 },
        style: { fill: { kind: "solid", color: fill }, radius: 999 },
        interactive: { label: name },
      },
      frame: (s) => lane(runnerFrames(s), s),
      font: (s) => clamp(r(runnerFrames(s).playerH * 0.28), 9, 28),
    });
  const right = (s: Size, w: number) => s.width - w - pad(s);
  const low = token("hazardLow", "Peligro bajo (saltar)", "▲", "#B91C1C", (f, s) => {
    const d = r(f.playerH * 0.42);
    return frameOf(right(s, d), f.groundY - d, d, d);
  });
  const high = token("hazardHigh", "Peligro alto (agacharse)", "●", "#7C2D12", (f, s) => {
    const d = r(f.playerH * 0.4);
    return frameOf(right(s, d), f.groundY - f.playerH * 1.05, d, d);
  });
  const boost = token("pickup", "Energía para recoger", "+", "#15803D", (f, s) => {
    const d = r(f.playerH * 0.36);
    return frameOf(right(s, d), f.groundY - f.playerH * 0.62, d, d);
  });

  // HUD on a dark band: readable over every environment (day, sunset, night).
  b.layer(scene, {
    element: {
      id: b.id("hudBand"),
      type: "shape",
      name: "Fondo del marcador",
      shape: "rect",
      style: { fill: { kind: "solid", color: "#111827CC" } },
    },
    frame: (sz) => frameOf(0, 0, sz.width, band(sz) + pad(sz)),
  });
  // HUD: energy bar (pops on a pickup) and points.
  const popTrack = b.id("pickupPop");
  const bar = b.layer(scene, {
    element: {
      id: b.id("energyBar"),
      type: "component",
      name: "Energía",
      component: "ia.progress@1",
      props: { source: "variable", style: "bar", variable: energy, max: 100, color: "#16A34A" },
      interactive: { label: "Energía" },
      animations: [
        { ...expandPreset({ preset: "pop", trackId: popTrack, at: 0, duration: 300 }), start: { mode: "manual" } },
      ],
    },
    frame: (s) => frameOf(pad(s), pad(s), s.width * 0.45, clamp(band(s) * 0.45, 8, 16)),
  });
  b.layer(
    scene,
    textLayer(
      b.id("scoreText"),
      "Marcador",
      { runs: [{ text: "Puntos " }, { bind: { var: score, format: "integer" } }] },
      WHITE,
      700,
      (s) => frameOf(s.width * 0.55, pad(s) / 2, s.width * 0.45 - pad(s), band(s)),
      (s) => clamp(r(band(s) * 0.5), 10, 20),
      "right"
    )
  );

  // Controls on the ground band (keyboard: Space/↑ jump, ↓ duck).
  const control = (prefix: string, name: string, label: string, side: 0 | 1) =>
    b.layer(scene, {
      ...buttonLayer(b.id(prefix), name, label, "#111827CC", WHITE, (s) => {
        const g = runnerFrames(s).groundY;
        const h = clamp((s.height - g) * 0.5, 22, 52);
        const w = clamp(s.width * 0.4, 72, 180);
        const y = g + (s.height - g - h) / 2;
        return frameOf(side === 0 ? pad(s) : s.width - pad(s) - w, y, w, h);
      }),
      font: (s) => clamp(r(Math.min(s.width / 22, (s.height - runnerFrames(s).groundY) * 0.22)), 10, 16),
    });
  const jumpBtn = control("jumpButton", "Botón saltar", "▲ Saltar", 0);
  const duckBtn = control("duckButton", "Botón agacharse", "▼ Agacharse", 1);

  b.rule({
    id: b.id("hitFeedback"),
    on: { type: "game.event", event: "hazard" },
    do: [{ type: "animation.play", target: player, track: hitTrack }],
  });
  b.rule({
    id: b.id("pickupFeedback"),
    on: { type: "game.event", event: "pickup" },
    do: [{ type: "animation.play", target: bar, track: popTrack }],
  });

  const res = results(b, scene, score, "Puntos:", accent, link);
  const segments = SEGMENT_NAMES.map((name, i) => ({
    id: b.id(`segment${String(i + 1)}`),
    name,
    durationMs: b.tune.durationMs === undefined ? 15_000 : r(playTime(b.tune.durationMs) / SEGMENT_NAMES.length),
    ...(i > 0 ? { speed: 1 + i * 0.15 } : {}),
    show: [skies[i] as string, scenery[i] as string],
  }));
  const speedOf = (l: { readonly size: Size }) => r(clamp(l.size.width * 0.55, 60, 600));
  const layouts = b.layoutSizes();
  const [master, ...others] = layouts;
  return {
    intro: start.scene,
    scenes: [scene],
    results: [res.win, res.lose],
    game: {
      id: b.id("runnerGame"),
      name: "Carrera",
      scenes: [scene],
      start: "onEnter",
      score,
      win: { when: "finish" },
      scoring: { hit: 0, miss: 0, missCostsLife: false },
      results: { win: res.win, lose: res.lose, delayMs: 700 },
      runner: {
        player: {
          element: player,
          jump: { height: 1.5, durationMs: 720 },
          duck: { height: 0.5, durationMs: 520 },
          hitbox: { x: 0.12, y: 0.06 },
          invulnerableMs: 900,
          states: { duck: "duck", hit: "hit" },
        },
        speed: master ? speedOf(master) : 160,
        ...(others.length > 0 ? { speedByLayout: Object.fromEntries(others.map((l) => [l.id, speedOf(l)])) } : {}),
        direction: "left",
        layers: [
          ...scenery.map((id) => ({ element: id, factor: 0.35, loop: true })),
          { element: ground, factor: 1, loop: true },
        ],
        segments,
        fadeMs: 1_200,
        spawns: [
          {
            id: b.id("lowHazards"),
            kind: "hazard",
            templates: [low],
            everyMs: { min: 1_700, max: 3_000 },
            maxActive: 3,
            effect: { energy: -20 },
          },
          {
            id: b.id("highHazards"),
            kind: "hazard",
            templates: [high],
            everyMs: { min: 2_600, max: 4_200 },
            startMs: 4_000,
            maxActive: 2,
            effect: { energy: -20 },
          },
          {
            id: b.id("pickups"),
            kind: "pickup",
            templates: [boost],
            everyMs: { min: 2_200, max: 3_800 },
            startMs: 1_500,
            maxActive: 2,
            effect: { energy: 25, score: 10 },
          },
        ],
        energy: { var: energy, drainPerSecond: 1 },
        controls: { jump: jumpBtn, duck: duckBtn },
      },
    },
  };
}

const BUILDERS: Readonly<Record<GameStarterKind, (b: Builder, accent: string, link: string | undefined) => Built>> = {
  find: findTheDifference,
  pairs: matchingPairs,
  tap: tapCatch,
  quiz,
  drag: dragToTarget,
  runner,
};

/**
 * Operations that add a complete, editable advergame of `kind` to `doc`
 * (intro scene → game scene(s) → win / lose scenes). Throws if the document
 * already declares a game (one game per document, like the survey).
 * The CTA on the result scenes opens the document's first landing link, when there is one.
 */
export function gameStarter(
  doc: CreativeDocument,
  kind: GameStarterKind,
  options: GameStarterOptions = {}
): GameStarter {
  if (doc.game) throw new Error("the document already has a game");
  const b = new Builder(doc, options);
  const accent = options.accent ?? DEFAULT_ACCENT;
  const built = BUILDERS[kind](b, accent, doc.links[0]?.id);
  b.ops.push({ op: "setGame", game: built.game });
  if (options.makeInitial) b.ops.push({ op: "setInitialScene", id: built.intro });
  return {
    ops: b.ops,
    introSceneId: built.intro,
    gameSceneIds: built.scenes,
    resultSceneIds: built.results,
    game: built.game,
  };
}
